import React, { useEffect, useMemo, useState, useCallback } from "react";
import { fetchProps, fetchSummary, toggleWatchlist, fetchWatchlist, getUserToken } from "../lib/api";
import { api } from "../lib/api";
import { PropCard } from "../components/PropCard";
import { ParlayCard } from "../components/ParlayCard";
import { SportTabs } from "../components/SportTabs";
import { LiveBadge } from "../components/LiveBadge";
import { CaretDown, MagnifyingGlass, Funnel } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIERS = ["ALL", "PRIME", "STRONG", "LEAN"];
const SORTS = [
  { key: "confidence", label: "Confidence" },
  { key: "hit_count", label: "Hit Rate" },
  { key: "line", label: "Line" },
];

export default function Dashboard() {
  const [sport, setSport] = useState("ALL");
  const [tier, setTier] = useState("ALL");
  const [sort, setSort] = useState("confidence");
  const [search, setSearch] = useState("");
  const [props, setProps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [dataSource, setDataSource] = useState("demo");
  const [liveMeta, setLiveMeta] = useState(null);
  const [watched, setWatched] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const token = getUserToken();

  const refreshWatchlist = useCallback(async () => {
    const data = await fetchWatchlist(token);
    setWatched(new Set(data.props.map((p) => p.id)));
  }, [token]);

  const loadAll = useCallback(() => {
    fetchSummary().then((s) => {
      setSummary(s);
      setDataSource(s.source || "demo");
      setLiveMeta(s.live_meta || null);
    }).catch(() => {});
    // Weekly hit-rate
    api.get("/results/summary", { params: { window: "week" } })
      .then(({ data }) => setWeeklyStats(data))
      .catch(() => {});
    setLoading(true);
    fetchProps({ sport, tier, sort, limit: 100 })
      .then((d) => {
        setProps(d.props);
        if (d.source) setDataSource(d.source);
        if (d.live_meta) setLiveMeta(d.live_meta);
      })
      .finally(() => setLoading(false));
  }, [sport, tier, sort]);

  useEffect(() => {
    refreshWatchlist();
  }, [refreshWatchlist]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const filtered = useMemo(() => {
    if (!search.trim()) return props;
    const q = search.toLowerCase();
    return props.filter((p) =>
      (p.player?.name || "").toLowerCase().includes(q) ||
      (p.stat_type || "").toLowerCase().includes(q) ||
      (p.player?.team || "").toLowerCase().includes(q)
    );
  }, [props, search]);

  const handleWatch = async (propId) => {
    const wasWatched = watched.has(propId);
    const res = await toggleWatchlist(token, propId);
    setWatched((prev) => {
      const next = new Set(prev);
      if (res.status === "added") next.add(propId);
      else next.delete(propId);
      return next;
    });
    toast(wasWatched ? "Removed from My Picks" : "Added to My Picks", {
      description: res.status === "added" ? "It's on your slate now." : "No longer in your slate.",
    });
  };

  const topPick = filtered[0];

  return (
    <div className="bg-[#0A0A0A] min-h-screen">
      {/* Hero strip */}
      <section className="stadium-hero noise relative border-b border-[#27272A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
          <div className="overline mb-3">Today's Board • {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</div>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-[0.95]">
            GP PICKS<br />
            <span style={{ color: "#FF4500" }}>LIVE</span> ODDS, SHARP EDGE.
          </h1>
          <p className="text-[#A1A1AA] max-w-xl mt-4 text-sm sm:text-base">
            Live over/under lines for upcoming games across NBA, NFL, NHL & MLB — only in-season sports shown. Ranked by implied edge plus last-10 trend simulation.
          </p>

          <div className="mt-6">
            <LiveBadge source={dataSource} meta={liveMeta} onRefreshed={loadAll} />
          </div>

          {summary && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px mt-8 bg-[#27272A] border border-[#27272A] max-w-2xl">
              <Stat label="Total Props" value={summary.total_props} />
              <Stat label="Prime" value={summary.locks} accent="#FF4500" />
              <Stat label="Strong" value={summary.strong} />
              <Stat
                label="Weekly Hit Rate"
                value={weeklyStats && (weeklyStats.wins + weeklyStats.losses) > 0
                  ? `${weeklyStats.accuracy}%`
                  : "—"}
                accent={weeklyStats && weeklyStats.accuracy >= 55 ? "#22c55e" : weeklyStats && weeklyStats.accuracy > 0 && weeklyStats.accuracy < 50 ? "#ef4444" : undefined}
                sub={weeklyStats && (weeklyStats.wins + weeklyStats.losses) > 0 ? `${weeklyStats.wins}-${weeklyStats.losses}` : "no results yet"}
                testid="stat-weekly-hit-rate"
              />
            </div>
          )}
        </div>
      </section>

      {/* Sport Tabs */}
      <section className="border-b border-[#27272A] bg-[#0A0A0A] sticky top-16 z-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <SportTabs value={sport} onChange={setSport} counts={summary?.by_sport || {}} />
        </div>
      </section>

      {/* Toolbar */}
      <section className="border-b border-[#27272A] bg-[#0A0A0A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
          <div className="flex items-center gap-2 flex-1 max-w-md border border-[#27272A] px-3 h-10">
            <MagnifyingGlass size={16} color="#A1A1AA" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search player, team, or stat..."
              className="bg-transparent outline-none text-sm text-white placeholder:text-[#52525b] flex-1 font-[Manrope]"
              data-testid="prop-search-input"
            />
          </div>
          <div className="flex items-center gap-3">
            <FilterPill label="Tier" value={tier} options={TIERS} onChange={setTier} testid="filter-tier" />
            <FilterPill label="Sort" value={sort} options={SORTS.map((s) => s.key)} labels={SORTS.reduce((acc, s) => ({ ...acc, [s.key]: s.label }), {})} onChange={setSort} testid="filter-sort" />
            <div className="hidden md:flex items-center gap-1.5 overline">
              <Funnel size={14} />
              <span>{filtered.length} props</span>
            </div>
          </div>
        </div>
      </section>

      {/* Top pick spotlight */}
      {topPick && (
        <section className="border-b border-[#27272A]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-6">
            <div className="overline mb-2">⚡ Top Live Edge Today</div>
            <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6 items-start">
              <div className="border border-[#FF4500] bg-[#121212] p-6">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="overline text-[#FF4500]">SPOTLIGHT • {topPick.tier}</div>
                    <h2 className="font-head text-3xl text-white mt-1">{topPick.player?.name}</h2>
                    <div className="text-[#A1A1AA] text-sm mt-1">
                      {topPick.player?.sport} • {topPick.player?.team} {topPick.player?.is_home ? "vs" : "@"} {topPick.player?.opponent}
                    </div>
                  </div>
                  <div className={`px-4 py-2 font-head text-lg uppercase ${topPick.pick === "OVER" ? "pick-over" : "pick-under"}`}>
                    {topPick.pick} {topPick.line} {topPick.stat_type}
                  </div>
                </div>
                <div className="text-[#A1A1AA] text-sm leading-relaxed">{topPick.reasoning}</div>
                <div className="grid grid-cols-3 gap-4 mt-5 border-t border-[#27272A] pt-4">
                  <Stat label="Confidence" value={`${topPick.confidence}%`} accent="#FF4500" />
                  <Stat label="L10 Hit" value={`${topPick.hit_count}/${topPick.hit_total}`} />
                  <Stat label="L5 Avg" value={topPick.last_5_avg} />
                </div>
              </div>
              <div className="space-y-3">
                <div className="overline">How we score props</div>
                <ul className="space-y-2 text-sm text-[#A1A1AA]">
                  <li className="flex gap-3"><span className="text-[#FF4500] font-head">01</span> Last-10 hit rate against the line.</li>
                  <li className="flex gap-3"><span className="text-[#FF4500] font-head">02</span> Last-5 average vs season trend.</li>
                  <li className="flex gap-3"><span className="text-[#FF4500] font-head">03</span> Distance of recent form from the posted line.</li>
                </ul>
                <p className="text-xs text-[#52525b] pt-3 border-t border-[#27272A] mt-3">
                  Picks ranked by composite confidence. Always bet responsibly.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Props grid */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        <ParlayCard onAddedToWatchlist={refreshWatchlist} />
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="overline">Prop Board</div>
            <h2 className="font-head text-2xl text-white mt-1">{sport === "ALL" ? "All Sports" : sport} • {filtered.length} Picks</h2>
          </div>
        </div>
        {loading ? (
          <div className="text-[#A1A1AA] text-sm py-20 text-center" data-testid="props-loading">Loading the board…</div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center max-w-lg mx-auto" data-testid="props-empty">
            <div className="overline mb-3 text-[#FF4500]">No Picks Available Right Now</div>
            <p className="text-[#A1A1AA] text-sm leading-relaxed">
              {props.length === 0
                ? "Sportsbooks haven't posted lines for the upcoming slate yet — this usually happens 12–24 hours before first pitch/tip-off. Check back soon; picks refresh automatically every 30 minutes."
                : "No props match these filters. Try switching sport or tier."}
            </p>
            {props.length === 0 && liveMeta?.in_season_sports?.length > 0 && (
              <div className="mt-4 text-xs text-[#71717A]">
                In-season: {liveMeta.in_season_sports.join(" · ")} · Last refresh: {liveMeta.updated_at ? new Date(liveMeta.updated_at).toLocaleTimeString() : "—"}
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="props-grid">
            {filtered.map((p, i) => (
              <PropCard
                key={p.id}
                prop={p}
                index={i}
                onToggleWatch={handleWatch}
                isWatched={watched.has(p.id)}
                onCreatorPinChange={loadAll}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, accent, sub, testid }) {
  return (
    <div className="bg-[#0A0A0A] p-4" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-data text-2xl mt-1" style={{ color: accent || "#fff" }}>{value}</div>
      {sub && <div className="overline mt-0.5" style={{ color: "#71717A" }}>{sub}</div>}
    </div>
  );
}

function FilterPill({ label, value, options, labels = {}, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 h-10 border border-[#27272A] text-sm hover:border-white/30 transition-colors"
        data-testid={`${testid}-button`}
      >
        <span className="overline">{label}</span>
        <span className="font-data text-white text-sm">{labels[value] || value}</span>
        <CaretDown size={12} color="#A1A1AA" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 min-w-[160px] bg-[#0A0A0A] border border-[#27272A] z-20" data-testid={`${testid}-menu`}>
            {options.map((opt) => (
              <button
                key={opt}
                onClick={() => { onChange(opt); setOpen(false); }}
                className={`block w-full text-left px-4 py-2 text-sm hover:bg-[#1a1a1a] font-data ${value === opt ? "text-[#FF4500]" : "text-white"}`}
                data-testid={`${testid}-opt-${opt}`}
              >
                {labels[opt] || opt}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
